// This is being used in the main sidebar to toggle menu items
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["sidebar", "content", "logo", "textLabel", "collapseButton", "collapseIcon"]

  connect() {
    this.collapsed = this.getSavedState() || false
    this.checkScreenSize()
    this.setupMediaQuery()
    this.updateSidebar()
  }

  setupMediaQuery() {
    this.mediaQuery = window.matchMedia("(max-width: 1279px)")
    this.mediaQuery.addEventListener('change', this.handleScreenChange.bind(this))
    this.handleScreenChange()
  }

  handleScreenChange() {
    if (this.mediaQuery.matches && !this.collapsed) {
      // Auto-collapse on small screens only if user hasn't manually set preference
      if (!localStorage.getItem('sidebar-collapsed')) {
        this.collapsed = true
        this.saveState()
        this.updateSidebar()
      }
    }
  }

  checkScreenSize() {
    // Check if we're on a small screen initially
    // Only auto-collapse if user hasn't set a preference
    if (window.innerWidth < 1280 && !localStorage.getItem("sidebar-collapsed")) {
      this.collapsed = true
      this.saveState()
    }
  }

  toggle() {
    this.collapsed = !this.collapsed
    this.saveState()
    this.updateSidebar()
  }

  updateSidebar() {
    const sidebar = this.sidebarTarget
    const textLabels = this.textLabelTargets
    const logo = this.logoTarget
    const collapseButton = this.collapseButtonTarget
    const collapseIcon = this.collapseIconTarget
    const sidebarCollapseElement = sidebar.querySelector('li[data-controller="sidebar-collapse"]')

    if (this.collapsed) {
      // Collapsed state: narrow sidebar, hide text and logo
      sidebar.classList.remove("w-1/6")
      sidebar.classList.add("w-16")
      
      textLabels.forEach(label => {
        label.classList.add("hidden")
      })
      
      logo.classList.add("hidden")
      collapseButton.setAttribute("aria-expanded", "false")
      
      // Hide the sidebar-collapse element when collapsed
      if (sidebarCollapseElement) {
        sidebarCollapseElement.classList.add("hidden")
      }
      
      // Update icon to arrow-right (pointing right when collapsed)
      this.updateIcon("arrow-right")
      
      // Update nav padding for collapsed state
      const nav = sidebar.querySelector("nav")
      if (nav) {
        nav.classList.remove("px-4")
        nav.classList.add("px-2")
      }
    } else {
      // Expanded state: full sidebar, show text and logo
      sidebar.classList.remove("w-16")
      sidebar.classList.add("w-1/6")
      
      textLabels.forEach(label => {
        label.classList.remove("hidden")
      })
      
      logo.classList.remove("hidden")
      collapseButton.setAttribute("aria-expanded", "true")
      
      // Show the sidebar-collapse element when expanded
      if (sidebarCollapseElement) {
        sidebarCollapseElement.classList.remove("hidden")
      }
      
      // Update icon to arrow-left (pointing left when expanded)
      this.updateIcon("arrow-left")
      
      // Restore nav padding for expanded state
      const nav = sidebar.querySelector("nav")
      if (nav) {
        nav.classList.remove("px-2")
        nav.classList.add("px-4")
      }
    }
  }

  getSavedState() {
    try {
      const saved = localStorage.getItem('sidebar-collapsed')
      return saved === 'true'
    } catch (error) {
      console.warn('Could not access localStorage:', error)
      return false
    }
  }

  saveState() {
    try {
      localStorage.setItem('sidebar-collapsed', this.collapsed.toString())
    } catch (error) {
      console.warn('Could not save to localStorage:', error)
    }
  }

  updateIcon(iconType) {
    const collapseIcon = this.collapseIconTarget
    const iconHtml = this.getIconSvg(iconType)
    collapseIcon.innerHTML = iconHtml
  }

  getIconSvg(iconType) {
    const icons = {
      "arrow-left": `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='#374151' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='icon icon-tabler icons-tabler-outline icon-tabler-arrow-left'><path stroke='none' d='M0 0h24v24H0z' fill='none'/><path d='M5 12l14 0' /><path d='M5 12l6 6' /><path d='M5 12l6 -6' /></svg>`,
      "arrow-right": `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='#374151' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='icon icon-tabler icons-tabler-outline icon-tabler-arrow-right'><path stroke='none' d='M0 0h24v24H0z' fill='none'/><path d='M5 12l14 0' /><path d='M13 18l6 -6' /><path d='M13 6l6 6' /></svg>`
    }
    return icons[iconType] || icons["arrow-left"]
  }

  disconnect() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleScreenChange.bind(this))
    }
  }
}